import { isSuppressed } from '@/types/domain'
import type { CaseLeg, Maybe } from '@/types/domain'

/* Who carries a leg, said once for the leg page's summary and the case's
 * «Плечи» card (S-10, AC #9).
 *
 * A leg is routinely run by units of more than one haulier — two trucks from
 * Silk Road Trans, one owner-driver; containers of two lines — and the leg's own
 * carrier is only the default a new unit is born with. So "the carrier" of a
 * leg is the set of DISTINCT carriers its units run under, every kind
 * together (S-18b), and one rule decides it everywhere it is printed:
 *
 *  - a unit with no carrier of its own runs under the leg's default (the
 *    payment default already falls back this way);
 *  - one distinct carrier is named; several are counted, and the names sit
 *    behind a disclosure;
 *  - a reader the server does not show counter-agent identity sees «Перевозчик
 *    скрыт» — decided by the session's `seesAgentIdentity`, and ALSO by any
 *    AD-6 marker arriving in the data, so neither signal alone can leak.
 *
 * Distinct by the directory link when there is one, else by the name folded:
 * a carrier picked from the directory and the same name typed off a waybill
 * before the picker existed are two records, and printing them as one would be
 * a guess.
 */

export type CarrierSummary =
  | { kind: 'hidden' }
  | { kind: 'none' }
  | { kind: 'one'; name: string }
  | { kind: 'several'; names: string[] }

/** One carrier reference as either a unit row or a leg carries it. */
export interface CarrierRef {
  carrierId?: Maybe<string>
  carrierName?: Maybe<string>
}

function text(value: Maybe<string> | undefined): string {
  return typeof value === 'string' ? value.trim() : ''
}

export function legCarriers({
  leg,
  units,
  seesAgentIdentity,
}: {
  /** The leg's default carrier. */
  leg: CarrierRef
  /** Every unit of every kind. */
  units: readonly CarrierRef[]
  seesAgentIdentity: boolean | undefined
}): CarrierSummary {
  const refs = [leg, ...units]
  if (
    seesAgentIdentity !== true ||
    refs.some((ref) => isSuppressed(ref.carrierId) || isSuppressed(ref.carrierName))
  ) {
    return { kind: 'hidden' }
  }

  const blank = (ref: CarrierRef) => text(ref.carrierId) === '' && text(ref.carrierName) === ''
  /* No units: the leg's default is the whole answer. With units, each blank
     one stands for the default and the default itself is not a separate
     carrier — a leg whose units all run under somebody else is carried by
     them. */
  const effective = units.length === 0 ? [leg] : units.map((unit) => (blank(unit) ? leg : unit))

  const names = new Map<string, string>()
  for (const ref of effective) {
    if (blank(ref)) continue
    const id = text(ref.carrierId)
    const name = text(ref.carrierName)
    const key = id !== '' ? `id:${id}` : `name:${name.toLocaleLowerCase()}`
    if (!names.has(key) || (names.get(key) === '' && name !== '')) names.set(key, name)
  }

  const distinct = [...names.values()]
  if (distinct.length === 0) return { kind: 'none' }
  if (distinct.length === 1) return { kind: 'one', name: distinct[0] ?? '' }
  return { kind: 'several', names: distinct }
}

/** The summary of a saved leg as the server serves it. */
export function legCarriersOf(
  leg: Pick<CaseLeg, 'carrierId' | 'carrierName' | 'units'>,
  seesAgentIdentity: boolean | undefined,
): CarrierSummary {
  return legCarriers({ leg, units: leg.units ?? [], seesAgentIdentity })
}
